// Sub-service pages under "Digital & Commerce".
// Rendered by pages/ServiceDetailPage.jsx at
// /capabilities/digital-commerce/<slug>

export const DIGITAL_SUBPAGES = [
  {
    slug: "performance-marketing-paid-media",
    image: "/photos/sub-performance-marketing-paid-media.jpg",
    imageAlt: "Indian performance marketers reviewing paid campaign dashboards",
    title: "Performance Marketing & Paid Media",
    metaDescription:
      "Performance marketing and paid media across Google, Meta, YouTube and LinkedIn — customer acquisition optimised for revenue, contribution and profitability.",
    heroSubhead: "Acquire customers profitably. Scale what works.",
    heroParas: [
      "Paid media can create growth quickly — but it can also scale inefficiency just as fast.",
      "BLEZZINGS helps businesses build and optimise performance marketing across Google, Meta, YouTube, LinkedIn and other relevant digital channels, with a clear focus on customer acquisition, qualified demand, revenue and profitability.",
      "We optimise beyond clicks and platform ROAS to improve the economics of acquiring customers.",
    ],
    heroCta: "Improve Your Performance Marketing",
    challengeLead: "When acquisition gets expensive, media is not always the real problem.",
    challengeBlocks: [
      {
        type: "paras",
        items: [
          "Performance marketing often starts well. Campaigns generate traffic. Leads arrive. Sales increase.",
          "Then growth becomes harder.",
        ],
      },
      {
        type: "list",
        items: [
          "CPMs rise",
          "CPCs increase",
          "CAC deteriorates",
          "ROAS falls",
          "Creative fatigues faster",
          "Retargeting pools stop scaling",
          "Lead quality declines",
          "Competitors bid more aggressively",
          "Conversion fails to improve",
          "More budget produces diminishing returns",
        ],
      },
      {
        type: "paras",
        items: [
          "The instinct is usually to optimise campaigns harder. Change audiences. Change bids. Change creatives. Change platforms.",
          "But the real constraint may sit somewhere else:",
        ],
      },
      {
        type: "list",
        items: [
          "Weak positioning",
          "The wrong target customer",
          "An uncompetitive offer",
          "Poor pricing",
          "Undifferentiated creative",
          "Low trust",
          "Weak landing pages",
          "Poor website conversion",
          "Low marketplace ratings",
          "Weak repeat purchase",
          "Or customer economics that simply cannot support the acquisition cost",
        ],
      },
      { type: "highlight", text: "Performance marketing works best when the entire acquisition system works." },
    ],
    perspectiveLead: "The objective is not cheaper traffic. It is more valuable customers.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Platform metrics matter. But they are intermediate indicators.",
          "A campaign with a strong click-through rate can still destroy value. A campaign with a high ROAS can still be unattractive if:",
        ],
      },
      {
        type: "list",
        items: [
          "Margins are weak",
          "Returns are high",
          "Customers never purchase again",
          "Discounts subsidise the sale",
          "Marketplace fees absorb contribution",
          "Lead quality is poor",
        ],
      },
      { type: "paras", items: ["We therefore connect:"] },
      { type: "highlight", text: "Audience → Proposition → Creative → Media → Landing Experience → Conversion → Revenue → Contribution → Lifetime Value" },
      {
        type: "paras",
        items: [
          "The right question is not: “How do we improve this campaign?”",
          "It is: “How do we acquire more commercially valuable customers at an economics the business can sustain?”",
        ],
      },
    ],
    transform: [
      { name: "Paid Search", body: "Capture high-intent demand across relevant search platforms." },
      { name: "Paid Social", body: "Build demand and acquire customers through Meta and other appropriate social platforms." },
      { name: "Shopping & Product Advertising", body: "Use product-led advertising to drive commerce and transaction intent." },
      { name: "Lead Generation", body: "Generate qualified opportunities rather than simply increasing enquiry volume." },
      { name: "Audience Strategy", body: "Identify, prioritise and test customer audiences systematically." },
      { name: "Remarketing & Retargeting", body: "Convert existing demand more effectively without over-relying on bottom-funnel audiences." },
      { name: "Creative Performance", body: "Develop and test messages, propositions, formats and creative territories that improve response." },
      { name: "Landing-Page Performance", body: "Ensure media and landing experiences work as one conversion system." },
      { name: "Media Allocation", body: "Shift investment toward the platforms, audiences and campaigns creating the greatest commercial return." },
      { name: "Measurement & Attribution", body: "Connect channel metrics to business outcomes." },
    ],
    approach: [
      { title: "Discover", body: "Analyse customers, economics, historical campaigns, creative, conversion, competitors and existing measurement." },
      { title: "Strategize", body: "Define acquisition objectives, priority audiences, channel roles, funnel stages and target economics." },
      { title: "Design", body: "Build campaign architecture, messaging, creative-testing plans, landing experiences and measurement frameworks." },
      { title: "Activate", body: "Launch campaigns across the appropriate paid channels and customer journeys." },
      { title: "Optimize", body: "Continuously improve audience, creative, media, conversion and budget allocation based on commercial performance." },
    ],
    deliverIntro: "Depending on scope:",
    deliver: [
      "Performance marketing audit",
      "Customer acquisition diagnostic",
      "Channel strategy",
      "Media plan",
      "Campaign architecture",
      "Audience framework",
      "Paid search campaigns",
      "Paid social campaigns",
      "Shopping campaigns",
      "YouTube advertising strategy",
      "LinkedIn advertising where relevant",
      "Lead-generation programmes",
      "Remarketing strategy",
      "Creative-testing framework",
      "Performance creative briefs",
      "Landing-page recommendations",
      "Funnel analysis",
      "Budget allocation model",
      "CAC framework",
      "ROAS framework",
      "Contribution-level performance analysis",
      "Attribution recommendations",
      "Performance dashboard",
      "Optimisation roadmap",
    ],
    outcomesIntro: "Performance marketing transformation can help create:",
    outcomes: [
      "Lower customer acquisition cost",
      "Higher ROAS",
      "More qualified leads",
      "Better lead-to-sale conversion",
      "Higher digital revenue",
      "Improved contribution from paid channels",
      "Better media productivity",
      "Greater creative effectiveness",
      "Stronger scalability",
      "Reduced dependence on one platform",
      "Better allocation of digital budgets",
      "Improved understanding of true acquisition economics",
    ],
    impact: [
      { metric: "28% Improvement in Marketing ROI", body: "Relevant leadership experience includes improving marketing ROI materially while simultaneously scaling a differentiated consumer business." },
      { metric: "5× Sales Growth", body: "The same business grew fivefold — reinforcing our belief that performance marketing works hardest when it is connected to a strong proposition, customer strategy and commercial model." },
    ],
    impactNote:
      "Selected outcomes delivered by BLEZZINGS leadership in prior operating roles and are not represented as BLEZZINGS client engagements.",
    whyLead: "We diagnose beyond the ad account.",
    whyParas: [
      "Performance agencies naturally focus on campaigns.",
      "BLEZZINGS can also look at:",
    ],
    whyListAfter: 1,
    whyList: [
      "Customer strategy",
      "Brand positioning",
      "Offer",
      "Pricing",
      "Marketplace performance",
      "D2C conversion",
      "Product pages",
      "CRM",
      "Retention",
      "Customer lifetime value",
    ],
    whyAfter: [
      "That matters because sometimes the best way to improve paid-media economics is not another campaign optimisation.",
      "It may require fixing the proposition, conversion experience or customer economics first.",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "CAC is rising",
      "ROAS is falling",
      "Paid media has stopped scaling",
      "Campaign performance is volatile",
      "Lead quality is poor",
      "Marketing spends heavily but struggles to show profit impact",
      "Meta or Google performance has plateaued",
      "Creative fatigue is increasing",
      "Conversion is weak after the click",
      "You are launching a new digital acquisition programme",
      "You want to professionalise performance measurement",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, CMOs, growth leaders, digital heads, e-commerce leaders and P&L owners.",
    faqs: [
      { q: "What is performance marketing?", a: "Performance marketing is digital marketing where campaigns are managed against measurable actions such as leads, customer acquisition, purchases, revenue or other defined business outcomes." },
      { q: "Which platforms can BLEZZINGS support?", a: "Depending on the customer, category and geography, platforms may include Google, Meta, YouTube, LinkedIn, marketplace media and other relevant paid digital channels." },
      { q: "Can BLEZZINGS reduce our customer acquisition cost?", a: "We can identify and address the major drivers of CAC, which may include media, targeting, creative, proposition, landing pages, pricing, conversion or retention." },
      { q: "Is ROAS the most important performance metric?", a: "Not always. ROAS should be interpreted alongside margin, contribution, returns, repeat purchase and customer lifetime value." },
      { q: "Do you manage creative as part of performance marketing?", a: "Yes. Creative testing and performance creative can be part of the engagement, supported by our Brand, Marketing & Communications capability." },
      { q: "Can you audit our existing agency or media account?", a: "Yes. We can assess account structure, audiences, creative, spend allocation, tracking, funnel performance and commercial outcomes." },
    ],
    insights: [
      "ROAS Can Lie",
      "CAC Is Not Just a Media Metric",
      "Why Performance Campaigns Stop Scaling",
      "The Landing Page Is Part of the Media Strategy",
      "Better Targeting Cannot Fix a Weak Proposition",
    ],
    related: [
      { title: "D2C, CRO & Digital Experience", body: "Improve what happens after the click.", to: "/capabilities/digital-commerce/d2c-cro-digital-experience" },
      { title: "Customer Decision & Journey Intelligence", body: "Understand what drives or prevents conversion.", to: "/capabilities/market-customer-intelligence/customer-decision-journey-intelligence" },
      { title: "Advertising, Content & Creative", body: "Create stronger performance messages and assets.", to: "/capabilities/brand-marketing-communications/advertising-content-creative" },
      { title: "CRM, Retention & Loyalty", body: "Improve the lifetime economics of acquired customers.", to: "/capabilities/digital-commerce/crm-retention-loyalty" },
      { title: "Performance Marketing for Consumer Brands", body: "D2C-focused acquisition built around unit economics.", to: "/capabilities/digital-commerce/d2c-performance-marketing" },
      { title: "Retail & D2C Perspective", body: "See how acquisition economics play out in consumer brands.", to: "/industries/retail-d2c-ecommerce" },
    ],
    ctaHeading: "Don't just optimise campaigns. Improve the economics of acquisition.",
    ctaBody:
      "We can help identify where performance is breaking down and build a more scalable system for acquiring commercially valuable customers.",
    ctaLabel: "Improve Your Performance Marketing",
  },

  {
    slug: "ecommerce-marketplace-growth",
    image: "/photos/sub-ecommerce-marketplace-growth.jpg",
    imageAlt: "Indian e-commerce team managing marketplace listings with products and a laptop",
    title: "E-Commerce & Marketplace Growth",
    metaDescription:
      "Grow profitably across Flipkart, Amazon, Noon and quick commerce — marketplace strategy, channel mix, pricing and contribution margin across platforms.",
    heroSubhead: "Win where customers are already shopping.",
    heroParas: [
      "Marketplaces provide extraordinary customer access.",
      "But visibility, sales and profitability increasingly depend on mastering the entire marketplace system — assortment, content, pricing, advertising, reviews, inventory and commercial economics.",
      "BLEZZINGS helps businesses grow across Amazon, Flipkart, Noon, quick commerce and other relevant marketplaces while keeping profitability at the centre.",
    ],
    heroCta: "Accelerate Your Marketplace Growth",
    challengeLead: "Marketplace sales can grow while the business gets weaker.",
    challengeBlocks: [
      { type: "paras", items: ["A marketplace dashboard may show increasing GMV.", "But beneath the headline sales number:"] },
      {
        type: "list",
        items: [
          "Advertising costs rise",
          "Discounts deepen",
          "Platform fees increase",
          "Competition intensifies",
          "Organic rankings weaken",
          "Returns reduce contribution",
          "Inventory goes out of stock",
          "Ratings remain weak",
          "Low-performing SKUs absorb advertising",
          "Promotions erode margin",
          "Brands lose control over pricing",
          "Sales become increasingly dependent on paid visibility",
        ],
      },
      {
        type: "paras",
        items: [
          "The result can be impressive topline growth and disappointing profitability.",
          "Marketplace growth therefore requires much more than listing products and running ads.",
        ],
      },
    ],
    perspectiveLead: "Marketplace success is a commercial system — not a catalogue-management task.",
    perspectiveBlocks: [
      { type: "paras", items: ["Marketplace performance depends on the interaction between:"] },
      { type: "highlight", text: "Product → Price → Content → Search → Reviews → Advertising → Availability → Conversion → Margin" },
      {
        type: "paras",
        items: [
          "Weakness in one area affects everything else.",
          "A strong advertising campaign cannot compensate indefinitely for:",
        ],
      },
      {
        type: "list",
        items: [
          "Poor ratings",
          "Weak product pages",
          "Uncompetitive pricing",
          "Low availability",
          "Wrong assortment",
          "Undifferentiated products",
        ],
      },
      {
        type: "paras",
        items: [
          "We therefore manage marketplaces around two questions: How do we win the customer — and can we win that customer profitably?",
        ],
      },
    ],
    transform: [
      { name: "Marketplace Strategy", body: "Define platform roles, assortment, economics and growth priorities." },
      { name: "Amazon Growth", body: "Build and optimise presence, content, advertising and commercial performance." },
      { name: "Flipkart Growth", body: "Strengthen marketplace execution for Indian e-commerce customers." },
      { name: "Noon Growth", body: "Build marketplace presence and growth strategy for relevant GCC markets." },
      { name: "Quick Commerce", body: "Assess and activate relevant quick-commerce opportunities where category economics support them." },
      { name: "Catalogue & Listings", body: "Improve discoverability, clarity and conversion." },
      { name: "Marketplace SEO", body: "Improve organic visibility within marketplace search." },
      { name: "Retail Media", body: "Use sponsored products, brands and other platform advertising strategically." },
      { name: "Pricing & Promotions", body: "Manage marketplace competitiveness without losing commercial discipline." },
      { name: "Ratings & Reviews", body: "Strengthen trust and conversion through better product and customer experience." },
      { name: "Inventory & Availability", body: "Reduce lost sales caused by stock-outs and poor supply planning." },
      { name: "Marketplace Profitability", body: "Track true contribution after advertising, promotions, commissions and fulfilment costs." },
    ],
    approach: [
      { title: "Discover", body: "Analyse marketplace sales, assortment, content, traffic, advertising, conversion, reviews, competition, inventory and unit economics." },
      { title: "Strategize", body: "Define priority marketplaces, products, customer opportunities and target economics." },
      { title: "Design", body: "Build marketplace content, pricing, media, promotions, catalogue and operational plans." },
      { title: "Activate", body: "Implement listings, marketplace advertising, promotional programmes and growth initiatives." },
      { title: "Optimize", body: "Continuously improve rankings, conversion, ad efficiency, availability and contribution." },
    ],
    deliver: [
      "Marketplace diagnostic",
      "Platform strategy",
      "Amazon growth plan",
      "Flipkart growth plan",
      "Noon growth plan",
      "Quick-commerce assessment",
      "Marketplace assortment strategy",
      "Catalogue architecture",
      "Listing optimisation",
      "Marketplace keyword strategy",
      "Product-title optimisation",
      "Bullet and description optimisation",
      "Image/A+ content recommendations",
      "Marketplace SEO",
      "Retail-media strategy",
      "Sponsored advertising campaigns",
      "Pricing framework",
      "Promotion calendar",
      "Ratings and review strategy",
      "Competitive marketplace benchmarking",
      "Inventory recommendations",
      "Marketplace KPI dashboard",
      "Unit-economics framework",
      "Profitability improvement roadmap",
    ],
    outcomesIntro: "Marketplace transformation can help create:",
    outcomes: [
      "Higher marketplace sales",
      "Better organic rankings",
      "Improved conversion",
      "Higher advertising efficiency",
      "Stronger product-page performance",
      "Better ratings and reviews",
      "Increased share within priority categories",
      "Fewer lost sales from stock-outs",
      "More disciplined promotions",
      "Improved contribution margin",
      "Reduced dependence on paid advertising",
      "More sustainable marketplace growth",
    ],
    impact: [
      { metric: "Top 3 Amazon Bestseller", body: "Relevant leadership experience includes helping build a differentiated consumer product into the Top 3 bestsellers within its Amazon category." },
      { metric: "+66% Sales Growth | 9-Point EBITDA Improvement", body: "Leadership experience also includes rebuilding an e-commerce business, increasing sales by 66% while improving EBITDA from -6% to +3%." },
    ],
    impactNote:
      "Selected outcomes delivered in prior operating roles and are not represented as BLEZZINGS client engagements.",
    whyLead: "We manage marketplace growth through a P&L lens.",
    whyParas: [
      "Marketplace specialists may optimise advertising, listings, catalogue and rankings.",
      "BLEZZINGS connects those activities to:",
    ],
    whyListAfter: 1,
    whyList: [
      "Portfolio strategy",
      "Pricing",
      "Margins",
      "Inventory",
      "Brand",
      "D2C",
      "Customer acquisition",
      "Channel conflict",
      "Profitability",
    ],
    whyAfter: [
      "That means we can distinguish between a marketplace that is growing and one that is creating value.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Amazon or Flipkart sales are not scaling",
      "You are entering Noon or GCC marketplaces",
      "Marketplace ad costs are rising",
      "ACOS is too high",
      "Organic ranking is weak",
      "Listings receive traffic but conversion is poor",
      "Competitors dominate search results",
      "Ratings and reviews constrain conversion",
      "Promotions are eroding margin",
      "You are launching marketplace-first products",
      "E-commerce sales are growing but profitability is weak",
      "You need to decide between marketplace and D2C investment",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, e-commerce heads, marketplace teams, category leaders, CMOs and P&L owners.",
    faqs: [
      { q: "Which marketplaces does BLEZZINGS support?", a: "Depending on geography and category, engagements can include Amazon, Flipkart, Noon, quick-commerce platforms and other commercially relevant marketplaces." },
      { q: "Can you help us enter Noon?", a: "Yes. We can support market and marketplace strategy, assortment, listings, pricing, content, advertising and growth planning for relevant GCC markets." },
      { q: "Do you manage Amazon advertising?", a: "Yes. Marketplace media can include sponsored product and brand activity together with broader listing, pricing and conversion optimisation." },
      { q: "Can you improve marketplace ACOS?", a: "Potentially. ACOS is influenced by targeting and bids but also by organic ranking, product conversion, pricing, reviews, listing quality and product economics." },
      { q: "Do you help with ratings and reviews?", a: "We can help identify legitimate ways to improve the underlying customer experience, review generation processes and reputation management while remaining within marketplace policies." },
      { q: "How do you measure marketplace profitability?", a: "We look beyond GMV to include product margins, platform fees, advertising, discounts, returns, fulfilment and other relevant marketplace costs." },
    ],
    insights: [
      "Marketplace Sales ≠ Marketplace Profit",
      "Why ACOS Is Not Just an Advertising Problem",
      "The Product Page Is Your Marketplace Salesperson",
      "Amazon vs D2C: Where Should You Invest First?",
      "How to Enter Noon Without Repeating Your Amazon Mistakes",
    ],
    related: [
      { title: "Performance Marketing & Paid Media", body: "Extend demand generation beyond marketplace advertising.", to: "/capabilities/digital-commerce/performance-marketing-paid-media" },
      { title: "Product Portfolio Strategy", body: "Prioritise the products most likely to win online.", to: "/capabilities/strategy-commercial-transformation/product-portfolio-strategy" },
      { title: "Pricing & Revenue Management", body: "Protect economics across marketplaces and channels.", to: "/capabilities/strategy-commercial-transformation/pricing-revenue-management" },
      { title: "D2C, CRO & Digital Experience", body: "Build an owned channel alongside marketplace growth.", to: "/capabilities/digital-commerce/d2c-cro-digital-experience" },
      { title: "Amazon Marketplace Growth & Management", body: "Dedicated Amazon growth and account management.", to: "/capabilities/digital-commerce/amazon-marketplace-growth" },
      { title: "Retail & D2C Perspective", body: "See how commerce shifts affect your category.", to: "/industries/retail-d2c-ecommerce" },
    ],
    ctaHeading: "Grow marketplace sales without losing sight of marketplace profit.",
    ctaBody:
      "We can help improve how your business wins across Amazon, Flipkart, Noon and other relevant commerce platforms — from listing to advertising to contribution.",
    ctaLabel: "Accelerate Your Marketplace Growth",
  },

  {
    slug: "d2c-cro-digital-experience",
    image: "/photos/sub-d2c-cro-digital-experience.jpg",
    imageAlt: "Indian digital team reviewing a website checkout prototype on a large monitor",
    title: "D2C, CRO & Digital Experience",
    metaDescription:
      "D2C strategy, CRO and digital experience — turn website traffic into customers with journeys, product pages and checkout built to convert.",
    heroSubhead: "Turn digital traffic into customers.",
    heroParas: [
      "A D2C channel gives you control over the customer relationship.",
      "But traffic only creates value when visitors understand the proposition, trust the brand and complete the purchase.",
      "BLEZZINGS helps businesses build and optimise D2C experiences, landing pages and conversion journeys designed to turn more traffic into profitable customer relationships.",
    ],
    heroCta: "Improve Your Digital Conversion",
    challengeLead: "Most conversion problems happen after marketing has done its job.",
    challengeBlocks: [
      { type: "paras", items: ["A customer clicks.", "They reach the website.", "Then something breaks.", "Maybe:"] },
      {
        type: "list",
        items: [
          "The proposition is unclear",
          "The page is too crowded",
          "The benefits are buried",
          "Trust is weak",
          "Reviews are missing",
          "Pricing creates hesitation",
          "Shipping surprises the customer",
          "Navigation creates friction",
          "Mobile experience is poor",
          "Checkout is too complicated",
          "The product page fails to answer objections",
          "The customer simply has no reason to act now",
        ],
      },
      { type: "highlight", text: "More traffic poured into a weak experience simply creates more expensive leakage." },
    ],
    perspectiveLead: "Conversion is a customer-decision problem before it is a design problem.",
    perspectiveBlocks: [
      { type: "paras", items: ["Customers convert when enough uncertainty has been removed.", "They need to understand:"] },
      {
        type: "list",
        items: [
          "Is this for me?",
          "What problem does it solve?",
          "Why is it better?",
          "Can I trust this brand?",
          "Is it worth the price?",
          "What happens if something goes wrong?",
          "Why should I buy now?",
        ],
      },
      { type: "paras", items: ["That means CRO must connect:"] },
      { type: "highlight", text: "Customer Insight → Proposition → Content → UX → Trust → Offer → Checkout → Measurement" },
      { type: "paras", items: ["The objective is not simply to redesign pages.", "It is to make customer decisions easier."] },
    ],
    transform: [
      { name: "D2C Strategy", body: "Define the role owned commerce should play alongside marketplaces and offline channels." },
      { name: "Digital Customer Journey", body: "Map how customers move from landing through evaluation and purchase." },
      { name: "Product Detail Pages", body: "Build pages that answer customer questions and objections." },
      { name: "Landing Pages", body: "Create focused experiences for campaigns, products and segments." },
      { name: "Conversion Rate Optimisation", body: "Identify and remove friction that suppresses customer action." },
      { name: "UX/UI", body: "Improve usability without sacrificing persuasion and commercial clarity." },
      { name: "Merchandising", body: "Help customers discover the right products, bundles and recommendations." },
      { name: "Offer Architecture", body: "Structure bundles, discounts, guarantees and value cues strategically." },
      { name: "Checkout", body: "Reduce abandonment and unnecessary friction." },
      { name: "Testing", body: "Build systematic experimentation around high-impact hypotheses." },
    ],
    approach: [
      { title: "Discover", body: "Analyse traffic, behaviour, conversion, customer feedback, competitors, pages and checkout performance." },
      { title: "Strategize", body: "Identify the largest points of friction and prioritise commercial hypotheses." },
      { title: "Design", body: "Develop improved journeys, page structures, offers, messages and user experiences." },
      { title: "Activate", body: "Implement or support implementation of priority conversion improvements." },
      { title: "Optimize", body: "Run tests, measure customer behaviour and continuously improve conversion." },
    ],
    deliver: [
      "D2C diagnostic",
      "Digital customer-journey map",
      "Website conversion audit",
      "Funnel analysis",
      "Behaviour analysis",
      "Customer-friction map",
      "UX recommendations",
      "Navigation architecture",
      "Landing-page wireframes",
      "Product-page architecture",
      "PDP content recommendations",
      "Trust and social-proof framework",
      "Offer and bundle recommendations",
      "Cart optimisation",
      "Checkout recommendations",
      "Mobile conversion recommendations",
      "A/B testing roadmap",
      "CRO backlog",
      "Conversion dashboard",
      "30/60/90-day improvement roadmap",
    ],
    outcomesIntro: "D2C and CRO work can help create:",
    outcomes: [
      "Higher conversion rates",
      "Greater revenue per visitor",
      "Lower effective CAC",
      "Reduced bounce",
      "Lower cart abandonment",
      "Better product discovery",
      "Higher average order value",
      "Improved customer trust",
      "Stronger mobile performance",
      "Better returns from paid media",
      "Greater first-party customer ownership",
      "More sustainable D2C economics",
    ],
    impact: [
      { metric: "₹150Cr+ Consumer Business Scale", body: "Relevant leadership experience includes helping build and scale a differentiated D2C-led consumer business to more than ₹150Cr annual run-rate." },
      { metric: "28% Improvement in Marketing ROI", body: "Improved conversion and commercial alignment helped support more efficient growth alongside stronger marketing returns." },
    ],
    impactNote:
      "Selected outcomes reflect prior operating leadership experience rather than BLEZZINGS client engagements.",
    whyLead: "We don't treat CRO as a collection of button changes.",
    whyParas: [
      "Conversion problems often originate upstream.",
      "The issue may be:",
    ],
    whyListAfter: 1,
    whyList: [
      "Customer targeting",
      "Positioning",
      "Product",
      "Pricing",
      "Offer",
      "Creative",
      "Trust",
      "Marketplace expectations",
    ],
    whyAfter: [
      "Because BLEZZINGS works across those areas, we can distinguish between UX friction and a fundamentally weak commercial proposition.",
      "That leads to better conversion decisions than optimising pages in isolation.",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "Website traffic is growing but sales are not",
      "Paid media CAC is too high",
      "Product pages convert poorly",
      "Cart abandonment is high",
      "Mobile conversion trails desktop sharply",
      "D2C underperforms marketplaces",
      "Customers need too much information before buying",
      "The website looks good but does not sell",
      "You are building a new D2C channel",
      "You need to improve conversion before increasing media spend",
    ],
    whoAudience:
      "Typically relevant to founders, CEOs, e-commerce leaders, digital heads, growth teams, CMOs and product teams.",
    faqs: [
      { q: "What is CRO?", a: "Conversion Rate Optimisation is the systematic improvement of customer experiences to increase the proportion of visitors who complete desired actions." },
      { q: "Does CRO mean redesigning the website?", a: "Not necessarily. Often the biggest gains come from proposition clarity, page content, trust, offers or checkout rather than complete redesign." },
      { q: "Can BLEZZINGS build D2C websites?", a: "Depending on scope, we can support strategy, architecture, UX/UI, customer journey, conversion design and implementation coordination." },
      { q: "How do you identify conversion problems?", a: "We combine analytics, behavioural evidence, customer insight, page analysis, competitor benchmarking and commercial judgement." },
      { q: "Can conversion improvements reduce CAC?", a: "Yes. If more of the same traffic converts, the effective cost of acquiring a customer declines." },
      { q: "Do you run A/B tests?", a: "We can define hypotheses, prioritise tests, support implementation and interpret results depending on the technical setup and engagement scope." },
    ],
    insights: [
      "More Traffic Cannot Fix a Weak Website",
      "Conversion Is a Trust Problem Before It Is a Button Problem",
      "The Product Page Has One Job: Remove Uncertainty",
      "Why D2C Economics Break Even When ROAS Looks Good",
      "CRO Starts Before the Customer Lands on Your Website",
    ],
    related: [
      { title: "Performance Marketing & Paid Media", body: "Bring commercially relevant traffic into the conversion system.", to: "/capabilities/digital-commerce/performance-marketing-paid-media" },
      { title: "Customer Decision & Journey Intelligence", body: "Understand the barriers influencing customer choice.", to: "/capabilities/market-customer-intelligence/customer-decision-journey-intelligence" },
      { title: "Brand Strategy & Positioning", body: "Strengthen the proposition customers encounter.", to: "/capabilities/brand-marketing-communications/brand-strategy-positioning" },
      { title: "CRM, Retention & Loyalty", body: "Extend value beyond the first transaction.", to: "/capabilities/digital-commerce/crm-retention-loyalty" },
    ],
    ctaHeading: "Before buying more traffic, make more of your existing traffic convert.",
    ctaBody:
      "We can help identify where customers are dropping, why they hesitate and what needs to change to turn your D2C channel into a stronger growth engine.",
    ctaLabel: "Improve Your Digital Conversion",
  },

  {
    slug: "crm-retention-loyalty",
    image: "/photos/sub-crm-retention-loyalty.jpg",
    imageAlt: "Indian CRM team reviewing customer lifecycle dashboards on a screen",
    title: "CRM, Retention & Loyalty",
    metaDescription:
      "CRM, retention and loyalty programmes that turn first purchases into repeat revenue and higher customer lifetime value.",
    heroSubhead: "Turn transactions into relationships — and relationships into lifetime value.",
    heroParas: [
      "Acquiring a customer is expensive. Losing them after one purchase makes it even more expensive.",
      "BLEZZINGS helps businesses build CRM, lifecycle, retention and loyalty systems that increase repeat purchase, frequency and customer lifetime value.",
    ],
    heroCta: "Build Your Retention Engine",
    challengeLead: "Businesses spend heavily acquiring customers they barely speak to again.",
    challengeBlocks: [
      {
        type: "paras",
        items: [
          "Many digital businesses have sophisticated acquisition engines and surprisingly basic retention systems.",
          "The customer purchases. Receives an order confirmation. Maybe gets occasional promotional messages. Then disappears back into the acquisition market.",
          "Common problems include:",
        ],
      },
      {
        type: "list",
        items: [
          "Low repeat purchase",
          "Generic mass communication",
          "Poor customer segmentation",
          "No lifecycle journeys",
          "Excessive discount-led retention",
          "Underused first-party data",
          "Weak cross-sell",
          "Weak win-back programmes",
          "Loyalty programmes that simply distribute discounts",
          "CRM tools without a clear strategy",
          "Email and WhatsApp used as broadcasting channels",
          "Marketing focused overwhelmingly on new customers",
        ],
      },
      { type: "highlight", text: "The result is a growth engine that constantly needs to replace customers rather than compound them." },
    ],
    perspectiveLead: "The second purchase changes the economics of the first.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Acquisition cost should not be evaluated independently from customer lifetime value.",
          "A customer who purchases repeatedly may justify a much higher acquisition cost than a one-time buyer.",
          "Retention strategy therefore connects:",
        ],
      },
      { type: "highlight", text: "Customer → Lifecycle → Need → Trigger → Message → Offer → Repeat Behaviour → Lifetime Value" },
      {
        type: "paras",
        items: [
          "Different customers need different reasons to return.",
          "CRM should help the business become more relevant over time — not simply send more messages.",
        ],
      },
    ],
    transform: [
      { name: "CRM Strategy", body: "Define the role of customer data and lifecycle communication." },
      { name: "Lifecycle Design", body: "Build journeys from prospect to first purchase to repeat and loyalty." },
      { name: "Customer Segmentation", body: "Differentiate communication based on behaviour, value and needs." },
      { name: "Marketing Automation", body: "Trigger relevant communication at the right moments." },
      { name: "Email", body: "Build lifecycle and campaign programmes rather than generic newsletters." },
      { name: "WhatsApp & Messaging", body: "Use conversational channels strategically and responsibly." },
      { name: "Abandoned Journey Recovery", body: "Recover lost carts, leads and incomplete actions." },
      { name: "Cross-Sell & Upsell", body: "Increase customer value by identifying relevant additional needs." },
      { name: "Repeat Purchase", body: "Create systems around replenishment, reminders and repurchase occasions." },
      { name: "Loyalty", body: "Design programmes around behaviour and value rather than discounts alone." },
      { name: "Win-Back", body: "Identify and reactivate customers showing signs of churn." },
    ],
    approach: [
      { title: "Discover", body: "Analyse customer cohorts, repeat behaviour, lifecycle communication, CRM tools and existing retention economics." },
      { title: "Strategize", body: "Define priority customer segments, lifecycle stages and retention opportunities." },
      { title: "Design", body: "Build journeys, triggers, communication logic, offers and loyalty structures." },
      { title: "Activate", body: "Launch automated and campaign-based programmes across relevant CRM channels." },
      { title: "Optimize", body: "Track cohorts, repeat purchase, retention, frequency and lifetime value." },
    ],
    deliver: [
      "CRM diagnostic",
      "Customer-data assessment",
      "Lifecycle framework",
      "Customer segmentation",
      "RFM or behavioural segmentation where relevant",
      "Customer-value tiers",
      "Welcome journeys",
      "Lead-nurture journeys",
      "Abandoned-cart journeys",
      "Post-purchase journeys",
      "Repurchase journeys",
      "Cross-sell programmes",
      "Upsell programmes",
      "Email strategy",
      "WhatsApp strategy",
      "SMS strategy",
      "Marketing-automation architecture",
      "Loyalty programme design",
      "Win-back strategy",
      "Churn indicators",
      "CLV framework",
      "Retention dashboard",
    ],
    outcomesIntro: "Retention transformation can help create:",
    outcomes: [
      "Higher repeat purchase",
      "Improved retention",
      "Higher purchase frequency",
      "Increased average customer value",
      "Greater customer lifetime value",
      "Reduced reliance on paid acquisition",
      "Improved cross-sell",
      "Lower churn",
      "Stronger customer engagement",
      "More productive first-party data",
      "Better acquisition economics",
      "Greater long-term profitability",
    ],
    impact: [
      { metric: "Growth with Improved Marketing Economics", body: "Relevant leadership experience includes scaling a consumer business fivefold while improving marketing ROI by 28%. That experience shapes our retention philosophy: sustainable digital growth comes from improving both how customers are acquired and how much value is created after acquisition." },
    ],
    impactNote:
      "Selected outcome delivered in a prior operating role and not represented as a BLEZZINGS client engagement.",
    whyLead: "We connect retention to acquisition economics and the wider customer proposition.",
    whyParas: [
      "CRM agencies can optimise emails, automation and campaigns.",
      "BLEZZINGS can also address why customers may not be returning:",
    ],
    whyListAfter: 1,
    whyList: [
      "Product experience",
      "Pricing",
      "Proposition",
      "Assortment",
      "Service",
      "Customer journey",
      "Loyalty economics",
    ],
    whyAfter: [
      "That means retention strategy is grounded in customer behaviour and P&L value, not simply channel engagement.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Repeat purchase is weak",
      "CAC is rising",
      "Customer lists are growing but revenue from existing customers is not",
      "Email or WhatsApp performance is poor",
      "CRM tools are underused",
      "The business depends heavily on promotions",
      "Loyalty programmes generate little behaviour change",
      "Customer communication is generic",
      "You want to increase CLV",
      "You are building first-party customer relationships",
      "D2C growth needs to become more profitable",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, CRM heads, digital leaders, growth teams, e-commerce leaders and CMOs.",
    faqs: [
      { q: "What is CRM strategy?", a: "CRM strategy defines how customer data, segmentation, communication and technology will be used to strengthen relationships and increase customer value." },
      { q: "What is customer lifetime value?", a: "CLV estimates the value a customer generates over the duration of the relationship with the business." },
      { q: "Does retention always require a loyalty programme?", a: "No. Better product experience, lifecycle communication, cross-sell and repeat-purchase triggers may create more value than a points programme." },
      { q: "Can BLEZZINGS set up marketing automation?", a: "We can design automation logic, journeys and communication and support implementation within appropriate CRM and marketing-automation platforms." },
      { q: "Can WhatsApp be part of CRM?", a: "Yes, particularly in markets where customers use it heavily. It should be used with clear permission, relevance and frequency discipline." },
      { q: "How do you measure retention?", a: "Relevant measures can include repeat rate, purchase frequency, churn, cohort retention, customer lifetime value and contribution from existing customers." },
    ],
    insights: [
      "The Second Purchase Changes the Economics of the First",
      "Loyalty ≠ Points",
      "Why Most CRM Programmes Become Broadcast Engines",
      "Retention Is the Most Underused CAC Strategy",
      "Not Every Customer Deserves the Same Retention Investment",
    ],
    related: [
      { title: "Performance Marketing & Paid Media", body: "Acquire customers whose lifetime economics support scalable growth.", to: "/capabilities/digital-commerce/performance-marketing-paid-media" },
      { title: "Customer, Consumer & Buyer Insights", body: "Understand why customers stay, leave or purchase again.", to: "/capabilities/market-customer-intelligence/customer-buyer-insights" },
      { title: "D2C, CRO & Digital Experience", body: "Improve the experiences that shape customer satisfaction.", to: "/capabilities/digital-commerce/d2c-cro-digital-experience" },
      { title: "Segmentation & Targeting", body: "Identify the customer groups most worth retaining and growing.", to: "/capabilities/market-customer-intelligence/segmentation-targeting" },
    ],
    ctaHeading: "Your best next customer may already be in your database.",
    ctaBody:
      "We can help turn customer data and lifecycle communication into stronger retention, repeat purchase and lifetime value.",
    ctaLabel: "Build Your Retention Engine",
  },

  {
    slug: "seo-content-ai-search",
    image: "/photos/sub-seo-content-ai-search.jpg",
    imageAlt: "Indian content team working on search visibility reports and an editorial calendar",
    title: "SEO, Content & AI Search",
    metaDescription:
      "SEO, content and AI-search visibility built around commercial intent — be discoverable for the searches that create business.",
    heroSubhead: "Be discoverable when customers are actively looking.",
    heroParas: [
      "Search is changing. Customers now find answers, products and businesses across traditional search engines, marketplaces and AI-driven discovery experiences.",
      "BLEZZINGS helps businesses build organic visibility around the topics, questions and commercial searches that matter most to prospective customers.",
    ],
    heroCta: "Improve Your Search Visibility",
    challengeLead: "More organic traffic is not necessarily better organic growth.",
    challengeBlocks: [
      { type: "paras", items: ["SEO programmes often become obsessed with:"] },
      { type: "list", items: ["Ranking volume", "Traffic", "Publishing frequency", "Broad informational keywords"] },
      {
        type: "paras",
        items: [
          "But a business can attract large amounts of traffic from people who may never become customers.",
          "At the same time:",
        ],
      },
      {
        type: "list",
        items: [
          "High-intent pages remain weak",
          "Service pages lack authority",
          "Product pages are poorly optimised",
          "Important customer questions go unanswered",
          "Competitors dominate commercial searches",
          "Content is produced without topical structure",
          "Search and business strategy operate separately",
          "AI-driven discovery changes how customers access information",
        ],
      },
      {
        type: "paras",
        items: [
          "The objective should not be to attract everyone searching.",
          "It should be to become visible to the right customer at the right moment of intent.",
        ],
      },
    ],
    perspectiveLead: "Search strategy should begin with customer intent — not keyword volume.",
    perspectiveBlocks: [
      { type: "paras", items: ["Customers search because they are trying to:"] },
      { type: "list", items: ["Learn", "Compare", "Validate", "Solve", "Find", "Buy"] },
      { type: "paras", items: ["Different intentions require different content.", "We connect:"] },
      { type: "highlight", text: "Customer Need → Search Intent → Topic Authority → Content → Experience → Conversion" },
      { type: "paras", items: ["For commercial businesses, we prioritise search visibility where it can support:"] },
      { type: "list", items: ["Discovery", "Evaluation", "Trust", "Purchase", "Lead generation", "Marketplace sales"] },
      { type: "paras", items: ["Traffic is useful.", "Commercial relevance is more useful."] },
    ],
    transform: [
      { name: "SEO Strategy", body: "Define where organic search can create the greatest commercial value." },
      { name: "Commercial Keyword Strategy", body: "Prioritise high-intent searches rather than chasing traffic indiscriminately." },
      { name: "Technical SEO", body: "Ensure search engines can access, understand and index important content." },
      { name: "On-Page SEO", body: "Improve relevance, structure and usefulness of key pages." },
      { name: "E-Commerce SEO", body: "Improve product, collection and category discoverability." },
      { name: "Content Strategy", body: "Build authority around commercially relevant topics." },
      { name: "Topic Architecture", body: "Organise expertise into connected clusters rather than isolated articles." },
      { name: "Answer-Led Content", body: "Address the questions customers ask during consideration." },
      { name: "AI Search Readiness", body: "Structure useful, authoritative content clearly for evolving discovery experiences." },
      { name: "Local Search", body: "Improve visibility for location-driven businesses where relevant." },
      { name: "Measurement", body: "Connect organic visibility with leads, sales and commercial outcomes." },
    ],
    approach: [
      { title: "Discover", body: "Audit technical health, rankings, customer search behaviour, content, competitors and commercial pages." },
      { title: "Strategize", body: "Identify priority search territories, topics, customer questions and conversion opportunities." },
      { title: "Design", body: "Build keyword architecture, information structure, page plans and content systems." },
      { title: "Activate", body: "Optimise priority pages and develop commercially relevant content." },
      { title: "Optimize", body: "Track rankings, visibility, qualified traffic, conversions and emerging search behaviour." },
    ],
    deliver: [
      "SEO audit",
      "Technical SEO diagnostic",
      "Competitor search analysis",
      "Keyword research",
      "Commercial-intent keyword map",
      "Search-intent analysis",
      "Site architecture recommendations",
      "Service-page optimisation",
      "Product-page optimisation",
      "Category-page optimisation",
      "E-commerce SEO",
      "Local SEO recommendations",
      "Content strategy",
      "Topic clusters",
      "Editorial roadmap",
      "FAQ architecture",
      "Customer-question framework",
      "Internal-linking strategy",
      "Structured-content recommendations",
      "AI-search readiness recommendations",
      "Organic performance dashboard",
    ],
    outcomesIntro: "A stronger organic discovery system can help create:",
    outcomes: [
      "Greater high-intent visibility",
      "More qualified organic traffic",
      "Increased inbound enquiries",
      "Lower long-term dependence on paid media",
      "Higher product discoverability",
      "Stronger topical authority",
      "Better customer education",
      "Greater trust during evaluation",
      "Better service-page performance",
      "Improved organic conversion",
      "Stronger visibility across evolving search environments",
    ],
    impact: [
      { metric: "Building Efficient Digital Growth", body: "Relevant leadership experience includes improving marketing ROI by 28% while scaling a consumer business fivefold. This informs our approach to organic discovery: search should complement paid acquisition and strengthen the overall economics of customer discovery, rather than being treated as an isolated traffic channel." },
    ],
    impactNote:
      "Selected outcome delivered in a prior leadership role and not represented as a BLEZZINGS SEO client engagement.",
    whyLead: "We build search strategy around commercial strategy.",
    whyParas: [
      "SEO providers often begin with keywords.",
      "BLEZZINGS can begin further upstream:",
    ],
    whyListAfter: 1,
    whyList: [
      "Which customers matter?",
      "Which services or products are strategic?",
      "Which buying questions matter?",
      "Which topics should the brand credibly own?",
      "Which searches indicate genuine commercial intent?",
    ],
    whyAfter: [
      "Because our work spans market intelligence, brand, strategy and digital commerce, we can build content that is both useful to the customer and relevant to business growth.",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "Paid acquisition is becoming expensive",
      "Organic traffic is low",
      "Existing SEO traffic rarely converts",
      "Competitors dominate important searches",
      "Service pages are not ranking",
      "Product pages lack organic visibility",
      "You need stronger inbound lead generation",
      "Your content strategy lacks commercial direction",
      "Customers increasingly research before buying",
      "You want to build authority in a specialist field",
      "AI-led discovery is changing how customers find information",
    ],
    whoAudience:
      "Typically relevant to CEOs, founders, marketing leaders, digital heads, content teams, e-commerce leaders and B2B growth teams.",
    faqs: [
      { q: "What is SEO?", a: "SEO improves a website's ability to be discovered through organic search by making its content more relevant, accessible, useful and authoritative." },
      { q: "Does BLEZZINGS focus on traffic or leads?", a: "Commercial outcomes take priority. Traffic matters when it represents relevant customer demand or supports the customer decision journey." },
      { q: "What is commercial-intent SEO?", a: "It focuses on searches that indicate meaningful buying, comparison, evaluation or solution-seeking behaviour rather than broad informational volume alone." },
      { q: "Does BLEZZINGS provide content writing?", a: "Yes. Depending on engagement scope, we can develop strategic website, service, product, thought-leadership and search-led content." },
      { q: "What is AI Search optimisation?", a: "It involves creating clear, authoritative and well-structured content that can perform effectively as customers increasingly use AI-enabled search and answer experiences alongside conventional search." },
      { q: "Can BLEZZINGS guarantee rankings?", a: "No credible SEO provider can guarantee specific organic rankings because search results depend on many external factors. We focus on improving the factors a business can control." },
    ],
    insights: [
      "Traffic ≠ Demand",
      "Why Your Highest-Volume Keyword May Be Your Least Valuable",
      "SEO Should Start With the Customer, Not the Keyword Tool",
      "The Best B2B Content Answers Buying Questions",
      "Search Is Becoming an Answer Engine",
    ],
    related: [
      { title: "Market & Customer Intelligence", body: "Understand what customers actually seek and ask.", to: "/capabilities/market-customer-intelligence" },
      { title: "Customer Decision & Journey Intelligence", body: "Identify the questions customers need answered before buying.", to: "/capabilities/market-customer-intelligence/customer-decision-journey-intelligence" },
      { title: "Advertising, Content & Creative", body: "Build stronger editorial and thought-leadership content.", to: "/capabilities/brand-marketing-communications/advertising-content-creative" },
      { title: "Performance Marketing & Paid Media", body: "Connect organic and paid demand capture.", to: "/capabilities/digital-commerce/performance-marketing-paid-media" },
    ],
    ctaHeading: "Be visible for the searches that can actually create business.",
    ctaBody:
      "We can help build search and content authority around the questions, solutions and buying intentions that matter most to your prospective customers.",
    ctaLabel: "Improve Your Search Visibility",
  },

  {
    slug: "social-influencer-creator-marketing",
    image: "/photos/sub-social-influencer-creator-marketing.jpg",
    imageAlt: "Indian content creator recording a product video with a brand team",
    title: "Social, Influencer & Creator Marketing",
    metaDescription:
      "Social, influencer and creator marketing that builds brand relevance, demand and measurable commercial outcomes.",
    heroSubhead: "Build relevance where customers spend their attention.",
    heroParas: [
      "Social platforms shape discovery, conversation, trust and increasingly purchase.",
      "BLEZZINGS helps businesses build social, influencer and creator strategies that strengthen the brand, create demand and contribute to measurable commercial outcomes.",
    ],
    heroCta: "Build Your Social & Creator Strategy",
    challengeLead: "Publishing more content does not mean building a stronger brand.",
    challengeBlocks: [
      {
        type: "paras",
        items: [
          "Many organisations have active social channels.",
          "Few have a clear reason for every platform to exist.",
          "Common symptoms include:",
        ],
      },
      {
        type: "list",
        items: [
          "Content created simply to maintain frequency",
          "Follower growth disconnected from target customers",
          "Generic trend participation",
          "Inconsistent brand identity",
          "Influencer selection based primarily on follower count",
          "Engagement with little commercial relevance",
          "One-off creator campaigns",
          "Weak measurement",
          "Social and paid media working separately",
          "Little reuse of successful creator content",
          "Teams chasing virality rather than strategic relevance",
        ],
      },
      { type: "highlight", text: "The result is continuous activity without cumulative brand or business value." },
    ],
    perspectiveLead: "Social should earn attention. Creators should lend relevance and credibility.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Brands compete with millions of other pieces of content for attention.",
          "Interrupting the customer is not enough. You need a reason to deserve their attention.",
          "Strong social strategy connects:",
        ],
      },
      { type: "highlight", text: "Brand Territory → Audience Interest → Platform Behaviour → Content System → Community → Commercial Objective" },
      {
        type: "paras",
        items: [
          "Influencer and creator strategy adds another layer: Who has the credibility to carry this message — and why should their audience believe it?",
          "The objective is not to make the brand behave like every creator.",
          "It is to make the brand more relevant without becoming less distinctive.",
        ],
      },
    ],
    transform: [
      { name: "Social Strategy", body: "Define what role social should play in brand and commercial growth." },
      { name: "Platform Strategy", body: "Determine where the business should be active and why." },
      { name: "Content Strategy", body: "Build repeatable themes and formats around audience and brand relevance." },
      { name: "Social Creative", body: "Create content appropriate to platform behaviour without diluting the brand." },
      { name: "Community", body: "Build relationships and conversation rather than simply broadcasting." },
      { name: "Influencer Strategy", body: "Define the right role for creators within the wider marketing plan." },
      { name: "Influencer Selection", body: "Choose partners based on audience, credibility, fit and commercial relevance." },
      { name: "Creator Campaigns", body: "Develop strategic ideas creators can authentically bring to life." },
      { name: "UGC", body: "Build credible customer and creator-led content." },
      { name: "Social Commerce", body: "Connect discovery and influence with transaction where appropriate." },
      { name: "Creator Content for Paid Media", body: "Extend high-performing creator content beyond organic reach." },
      { name: "Measurement", body: "Assess brand, engagement, traffic, leads or commercial response depending on objective." },
    ],
    approach: [
      { title: "Discover", body: "Understand the audience, brand, category conversation, existing social performance and creator landscape." },
      { title: "Strategize", body: "Define channel roles, content territories, creator strategy and commercial objectives." },
      { title: "Design", body: "Build content systems, campaign concepts, creator briefs and measurement frameworks." },
      { title: "Activate", body: "Publish, partner, amplify and engage across selected channels and creator networks." },
      { title: "Optimize", body: "Track audience response, creative performance, creator effectiveness and commercial impact." },
    ],
    deliver: [
      "Social media audit",
      "Social strategy",
      "Audience analysis",
      "Platform-role framework",
      "Content pillars",
      "Content formats",
      "Editorial calendar",
      "Social creative system",
      "Community framework",
      "Social listening recommendations",
      "Influencer strategy",
      "Influencer selection criteria",
      "Creator shortlists",
      "Creator campaign concepts",
      "Creator briefs",
      "UGC framework",
      "Social-commerce recommendations",
      "Creator amplification strategy",
      "Paid creator-content strategy",
      "Campaign measurement framework",
      "Social performance dashboard",
    ],
    outcomesIntro: "Social and creator marketing can help create:",
    outcomes: [
      "Greater brand relevance",
      "Higher quality audience engagement",
      "Increased discovery",
      "Stronger social proof",
      "Greater message credibility",
      "Increased consideration",
      "Better-performing content",
      "Stronger campaign amplification",
      "More effective creator investment",
      "Greater customer advocacy",
      "Increased traffic and demand where commercially relevant",
    ],
    impact: [
      { metric: "Building a Differentiated Consumer Brand at Scale", body: "Relevant leadership experience includes helping scale a consumer business fivefold and reach a Top 3 marketplace bestseller position while improving marketing efficiency. This experience informs our social and creator philosophy: attention matters most when it reinforces a proposition customers ultimately choose." },
    ],
    impactNote:
      "Selected outcome delivered in a prior operating role rather than as a BLEZZINGS social-media client engagement.",
    whyLead: "We don't build social strategy around the algorithm alone.",
    whyParas: [
      "Platform behaviour changes constantly.",
      "The underlying brand and customer strategy should not.",
      "BLEZZINGS connects social and creators with:",
    ],
    whyListAfter: 2,
    whyList: [
      "Customer insight",
      "Brand positioning",
      "Marketing strategy",
      "Performance media",
      "Commerce",
      "Conversion",
    ],
    whyAfter: [
      "This allows us to use social as part of the growth system, rather than treating engagement as the final objective.",
    ],
    whoLead: "Relevant when:",
    whoBullets: [
      "Social activity lacks strategic direction",
      "Engagement is high but business impact is unclear",
      "Your content looks similar to competitors",
      "Influencer campaigns produce uncertain returns",
      "You need a stronger creator strategy",
      "Brand and performance social are disconnected",
      "Social content quality is inconsistent",
      "You are launching a new product",
      "You need customer advocacy or UGC",
      "You are entering a younger or digitally native audience",
      "You want to connect social activity with commerce",
    ],
    whoAudience:
      "Typically relevant to CMOs, founders, brand leaders, social teams, digital heads, category leaders and growth teams.",
    faqs: [
      { q: "What is social media strategy?", a: "Social media strategy defines which audiences, platforms, content territories and activities the brand should prioritise and what business or brand role each should perform." },
      { q: "How do you select influencers?", a: "Selection should consider audience fit, credibility, engagement quality, content style, brand fit, commercial objective and historical performance — not follower count alone." },
      { q: "Do you work with micro-influencers?", a: "Yes. The appropriate creator tier depends on the category, objective, customer and campaign economics." },
      { q: "Can influencer content be used in paid advertising?", a: "Where rights and agreements permit, creator content can be amplified through paid media and sometimes performs strongly because it combines authenticity with targeted reach." },
      { q: "Does BLEZZINGS manage social content?", a: "Depending on scope, we can develop strategy, editorial planning, creative systems, campaign content and ongoing content programmes." },
      { q: "How should social media be measured?", a: "Metrics should reflect the objective. These may include reach, attention, engagement quality, traffic, leads, product interest, conversion, brand metrics or commerce outcomes." },
    ],
    insights: [
      "Followers ≠ Customers",
      "Influence ≠ Audience Size",
      "Why Brands Should Stop Chasing Every Social Trend",
      "UGC Works Because Advertising Looks Like Advertising",
      "Your Social Strategy Needs a Reason to Exist",
    ],
    related: [
      { title: "Brand Strategy & Positioning", body: "Give social a distinctive strategic territory.", to: "/capabilities/brand-marketing-communications/brand-strategy-positioning" },
      { title: "Advertising, Content & Creative", body: "Build stronger campaign and content platforms.", to: "/capabilities/brand-marketing-communications/advertising-content-creative" },
      { title: "Performance Marketing & Paid Media", body: "Amplify high-performing social and creator content.", to: "/capabilities/digital-commerce/performance-marketing-paid-media" },
      { title: "E-Commerce & Marketplace Growth", body: "Connect influence and discovery with transaction.", to: "/capabilities/digital-commerce/ecommerce-marketplace-growth" },
    ],
    ctaHeading: "Build social influence that creates more than engagement.",
    ctaBody:
      "We can help connect brand strategy, content, creators and commerce into a social system designed to build relevance, demand and measurable value.",
    ctaLabel: "Build Your Social & Creator Strategy",
  },
  {
    slug: "amazon-marketplace-growth",
    image: "/photos/sub-amazon-marketplace-growth.jpg",
    imageAlt: "Marketplace growth dashboard for a consumer brand on Amazon",
    title: "Amazon Marketplace Growth & Management",
    metaDescription:
      "Amazon marketplace management for consumer brands in India — listing optimisation, Amazon advertising, pricing, promotions and contribution-margin improvement.",
    heroSubhead: "Win the shelf that never closes.",
    heroParas: [
      "Amazon is often a brand's largest digital storefront — and its least understood P&L.",
      "BLEZZINGS helps consumer brands, manufacturers and D2C businesses grow on Amazon and other marketplaces through connected strategy and execution: listings, advertising, pricing, portfolio, promotions and analytics managed against contribution, not just revenue.",
      "The goal is not simply more marketplace sales. It is profitable, defensible marketplace growth.",
    ],
    heroCta: "Discuss Your Amazon Growth Challenge",
    challengeLead: "Marketplace revenue can grow while marketplace economics quietly deteriorate.",
    challengeBlocks: [
      {
        type: "paras",
        items: [
          "Most brands on Amazon are busy. Listings are live, ads are running, orders arrive.",
          "But activity is not the same as profitable growth.",
        ],
      },
      {
        type: "list",
        items: [
          "Products are invisible in organic search",
          "Listings get traffic but don't convert",
          "Advertising cost of sales keeps climbing",
          "Weak content loses the buy box moment",
          "Product-level profitability is unknown",
          "Promotions spike sales and destroy margin",
          "Account growth has plateaued",
        ],
      },
      { type: "highlight", text: "Marketplace problems are rarely single-channel problems. They are assortment, content, pricing, advertising and economics problems — together." },
    ],
    perspectiveLead: "The marketplace rewards the brands that manage it as a business, not a channel.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Winning on Amazon requires the flywheel to work: visibility drives traffic, content drives conversion, reviews drive rank, rank drives visibility.",
          "We connect the commercial levers behind that flywheel:",
        ],
      },
      { type: "highlight", text: "Portfolio → Listing Quality → Pricing → Advertising → Conversion → Reviews → Contribution Margin" },
      {
        type: "paras",
        items: [
          "The right question is not: “How do we spend more on Amazon?”",
          "It is: “Which products, at what economics, deserve to win — and what must improve for them to win profitably?”",
        ],
      },
    ],
    transform: [
      { name: "Marketplace Strategy", body: "Define the role of Amazon and other marketplaces within the brand's channel and growth strategy." },
      { name: "Product Listing Optimisation", body: "Rebuild titles, imagery, content and keywords so listings rank and convert." },
      { name: "Amazon Advertising", body: "Structure and manage sponsored campaigns against target advertising cost of sales." },
      { name: "Portfolio & Pricing", body: "Decide which products to push, at what price, pack and margin." },
      { name: "Conversion Improvement", body: "Improve ratings, reviews, content and offer strength at the point of purchase." },
      { name: "Promotional Planning", body: "Use deals and events deliberately — for rank, trial and velocity, not habit." },
      { name: "Marketplace Analytics", body: "Build product-level visibility of revenue, advertising, fees and contribution." },
      { name: "Account Management", body: "Keep the operational engine healthy: inventory, buy box, catalogue and account health." },
    ],
    approach: [
      { title: "Discover", body: "Audit the account: portfolio, listings, advertising, pricing, fees, reviews and product-level profitability." },
      { title: "Strategize", body: "Prioritise hero products, define target economics and choose where to compete." },
      { title: "Design", body: "Rebuild listings, campaign architecture, pricing and promotional calendar." },
      { title: "Activate", body: "Execute listing, advertising and promotion changes with disciplined tracking." },
      { title: "Optimize", body: "Manage weekly against contribution margin, ACoS, conversion and share of search." },
    ],
    deliverIntro: "Depending on scope:",
    deliver: [
      "Marketplace account audit",
      "Product-level P&L and contribution analysis",
      "Listing content and keyword optimisation",
      "Amazon advertising restructure",
      "ACoS and budget frameworks",
      "Portfolio and pricing recommendations",
      "Promotional and events calendar",
      "Review and rating improvement plan",
      "Share-of-search tracking",
      "Competitor benchmarking",
      "Marketplace dashboard",
      "Growth roadmap",
    ],
    outcomesIntro: "Marketplace growth work can help create:",
    outcomes: [
      "Higher organic product visibility",
      "Better listing conversion",
      "Lower advertising cost of sales",
      "Improved product-level profitability",
      "Stronger review and rating profiles",
      "More efficient promotional spending",
      "Sustainable account growth",
      "Clearer marketplace decision-making",
    ],
    impact: [
      { metric: "Top 3 Amazon Bestseller", body: "Relevant leadership experience includes helping build a differentiated consumer product into the Top 3 bestsellers within its Amazon category." },
      { metric: "+66% Sales Growth | 9-Point EBITDA Improvement", body: "Leadership experience also includes rebuilding an e-commerce business, increasing sales by 66% while improving EBITDA from -6% to +3%." },
    ],
    impactNote:
      "Selected outcomes delivered in prior operating roles and are not represented as BLEZZINGS client engagements.",
    whyLead: "We manage marketplace growth through a P&L lens.",
    whyParas: [
      "Marketplace specialists often optimise advertising, listings or operations in isolation.",
      "BLEZZINGS connects marketplace execution to pricing, portfolio, brand and unit economics — so growth strengthens the business instead of subsidising the platform.",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "Amazon revenue has plateaued",
      "ACoS is rising faster than sales",
      "Listings get traffic but don't convert",
      "Product-level profitability is unclear",
      "Promotions are frequent but unprofitable",
      "You are launching a brand on marketplaces",
      "You want marketplace growth managed against contribution",
    ],
    related: [
      { title: "E-Commerce & Marketplace Growth", body: "The broader commerce growth system.", to: "/capabilities/digital-commerce/ecommerce-marketplace-growth" },
      { title: "D2C Performance Marketing", body: "Profitable customer acquisition for consumer brands.", to: "/capabilities/digital-commerce/d2c-performance-marketing" },
      { title: "Retail & D2C Industry View", body: "How commerce shifts affect your category.", to: "/industries/retail-d2c-ecommerce" },
    ],
    faqs: [
      { q: "Can BLEZZINGS manage our Amazon account end to end?", a: "Yes — depending on scope, we can support strategy, listing optimisation, advertising management, pricing and promotional planning, and performance tracking, working alongside your internal or operations teams." },
      { q: "How is this different from a typical marketplace agency?", a: "We manage the marketplace as a P&L, not a media account. Advertising, content, pricing, portfolio and promotions are optimised together against product-level contribution, not just revenue or ROAS." },
      { q: "We already sell on Amazon but margins are unclear. Can you help?", a: "That is a common starting point. We build product-level visibility of revenue, fees, advertising and contribution first, then decide where to invest, fix or exit." },
      { q: "Do you work with brands new to Amazon?", a: "Yes. For new entrants we define the marketplace strategy, launch portfolio, pricing, listing standards and advertising plan before spending scales." },
    ],
    ctaHeading: "Grow on Amazon with the margin intact.",
    ctaBody:
      "We can help you see which products deserve to win, fix what holds them back, and manage marketplace growth against contribution.",
    ctaLabel: "Discuss Your Amazon Growth Challenge",
  },
  {
    slug: "d2c-performance-marketing",
    image: "/photos/sub-d2c-performance-marketing.jpg",
    imageAlt: "D2C consumer brand team reviewing acquisition economics",
    title: "Performance Marketing for Consumer Brands",
    metaDescription:
      "D2C performance marketing agency for consumer brands — Meta and Google Ads, creative testing, CRO and retention managed against CAC and margin.",
    heroSubhead: "Acquisition that pays for itself.",
    heroParas: [
      "For a D2C or consumer brand, performance marketing is not a channel. It is the engine room of the P&L.",
      "BLEZZINGS helps D2C founders, e-commerce heads and consumer-brand teams acquire customers profitably — connecting Meta and Google campaigns, creative testing, conversion and retention to CAC, contribution margin and lifetime value.",
      "We optimise for customers who pay back, not clicks that look good in a dashboard.",
    ],
    heroCta: "Review Your Customer Acquisition Strategy",
    challengeLead: "When every rupee of growth is bought, economics decide who survives.",
    challengeBlocks: [
      {
        type: "paras",
        items: [
          "D2C growth often starts strong: early adopters convert cheaply, lookalikes scale, ROAS looks healthy.",
          "Then the easy audiences run out.",
        ],
      },
      {
        type: "list",
        items: [
          "Customer acquisition cost keeps rising",
          "ROAS looks fine but contribution is weak",
          "Conversion rates lag the traffic you pay for",
          "Creative testing is random, not systematic",
          "Remarketing pools exhaust quickly",
          "Repeat purchase doesn't cover acquisition cost",
        ],
      },
      { type: "highlight", text: "For consumer brands, the ad account is only half the system. Proposition, offer, pricing, conversion and retention decide the other half." },
    ],
    perspectiveLead: "Profitable D2C growth is an economics problem before it is a media problem.",
    perspectiveBlocks: [
      {
        type: "paras",
        items: [
          "Platform ROAS is an intermediate metric. A campaign can hit its ROAS target and still lose money once product margins, shipping, returns and discounts are counted.",
          "We manage acquisition against the numbers that decide survival:",
        ],
      },
      { type: "highlight", text: "CAC → Conversion → AOV → Contribution → Repeat Rate → LTV : CAC" },
      {
        type: "paras",
        items: [
          "The right question is not: “How do we get cheaper traffic?”",
          "It is: “What must be true — in proposition, creative, conversion and retention — for acquisition to pay for itself?”",
        ],
      },
    ],
    transform: [
      { name: "Paid Acquisition Strategy", body: "Define channel roles, funnel structure and target economics for Meta, Google and beyond." },
      { name: "Campaign Management", body: "Build and manage Meta and Google campaigns against commercial targets, not vanity metrics." },
      { name: "Creative Testing System", body: "Systematic creative development and testing tied to thumbstop, CTR, CVR and CAC." },
      { name: "Conversion Rate Optimisation", body: "Improve the post-click journey: landing pages, product pages, checkout and offers." },
      { name: "Attribution & Analytics", body: "Practical measurement that connects spend to revenue and contribution." },
      { name: "Retargeting & CRM", body: "Convert warm demand and improve repeat purchase through remarketing and lifecycle flows." },
      { name: "CAC & Profitability Management", body: "Manage budgets and scaling decisions against CAC, LTV and contribution thresholds." },
    ],
    approach: [
      { title: "Discover", body: "Audit account history, unit economics, creative, funnel conversion and retention." },
      { title: "Strategize", body: "Set target economics, audience priorities, channel roles and testing agenda." },
      { title: "Design", body: "Build campaign architecture, creative-testing plan, landing journeys and measurement." },
      { title: "Activate", body: "Launch and manage campaigns with disciplined creative and budget testing." },
      { title: "Optimize", body: "Scale what clears contribution thresholds; fix or cut what doesn't." },
    ],
    deliverIntro: "Depending on scope:",
    deliver: [
      "Acquisition economics diagnostic",
      "Channel and funnel strategy",
      "Meta and Google campaign architecture",
      "Creative testing framework",
      "Performance creative briefs",
      "Landing page and CRO roadmap",
      "Attribution setup recommendations",
      "Retargeting strategy",
      "CAC, LTV and contribution dashboard",
      "Budget allocation model",
      "Scaling playbook",
      "Weekly optimisation rhythm",
    ],
    outcomesIntro: "D2C performance marketing done properly can help create:",
    outcomes: [
      "Lower, more stable CAC",
      "ROAS that survives the P&L",
      "Higher conversion rates",
      "A repeatable creative-testing engine",
      "Better retention and repeat purchase",
      "Clearer scaling decisions",
      "Reduced dependence on one platform",
      "Growth the unit economics can sustain",
    ],
    impact: [
      { metric: "28% Improvement in Marketing ROI", body: "Relevant leadership experience includes improving marketing ROI materially while simultaneously scaling a differentiated consumer business." },
      { metric: "5× Sales Growth", body: "The same business grew fivefold — reinforcing our belief that performance marketing works hardest when connected to proposition, customer strategy and commercial model." },
    ],
    impactNote:
      "Selected outcomes delivered in prior operating roles and are not represented as BLEZZINGS client engagements.",
    whyLead: "We optimise the whole acquisition system, not just the ad account.",
    whyParas: [
      "Performance agencies naturally optimise inside the platform.",
      "BLEZZINGS can also address what sits outside it: proposition, pricing, offer, conversion experience, retention and customer economics — the levers that often decide whether paid acquisition can ever be profitable.",
    ],
    whoLead: "Particularly relevant when:",
    whoBullets: [
      "CAC keeps rising while margins shrink",
      "ROAS looks acceptable but the business loses money",
      "Creative output can't keep pace with fatigue",
      "Scaling spend stops scaling revenue",
      "Retention doesn't cover acquisition cost",
      "You are preparing to scale D2C spend seriously",
    ],
    related: [
      { title: "Performance Marketing & Paid Media", body: "The broader paid media capability.", to: "/capabilities/digital-commerce/performance-marketing-paid-media" },
      { title: "D2C, CRO & Digital Experience", body: "Fix what happens after the click.", to: "/capabilities/digital-commerce/d2c-cro-digital-experience" },
      { title: "CRM, Retention & Loyalty", body: "Make acquired customers come back.", to: "/capabilities/digital-commerce/crm-retention-loyalty" },
      { title: "Retail & D2C Industry View", body: "How commerce shifts affect your category.", to: "/industries/retail-d2c-ecommerce" },
    ],
    faqs: [
      { q: "How is this different from a regular performance marketing agency?", a: "We manage acquisition against D2C unit economics — CAC, contribution margin and lifetime value — not just platform ROAS. And we can fix what sits outside the ad account: proposition, pricing, conversion and retention." },
      { q: "Which platforms do you manage for consumer brands?", a: "Primarily Meta and Google, with others such as YouTube or marketplace advertising where the customer and category justify them." },
      { q: "Our ROAS looks fine but we're not profitable. Can you help?", a: "Yes — that is a common brief. We rebuild the view from platform ROAS to contribution per order, then align campaigns, creative, pricing and retention to it." },
      { q: "Do you also handle creative?", a: "We build the creative-testing system and briefs and can develop performance creative through the BLEZZINGS ecosystem, working with your brand guidelines." },
    ],
    ctaHeading: "Make acquisition pay for itself.",
    ctaBody:
      "We can help you see the real economics of every customer you acquire — and build a system that scales the profitable ones.",
    ctaLabel: "Review Your Customer Acquisition Strategy",
  },
];
